/**
 * Build styles
 */
require('./index.css').toString();
import { v4 as uuidv4 } from 'uuid';

/**
 * @class Warning
 * @classdesc User Input for Editor.js
 * @property {WarningData} data - User Input`s input and output data
 * @property {object} api - Editor.js API instance
 *
 * @typedef {object} WarningData
 * @description User Input`s input and output data
 * @property {string} title - warning`s title
 * @property {string} message - warning`s message
 *
 * @typedef {object} WarningConfig
 * @description User Input`s initial configuration
 * @property {string} titlePlaceholder - placeholder to show in warning`s title input
 * @property {string} messagePlaceholder - placeholder to show in warning`s message input
 */
export default class Warning {

    /**
     * Notify core that read-only mode is supported
     */
    static get isReadOnlySupported() {
        return true;
    }

    /**
     * Get Toolbox settings
     *
     * @public
     * @returns {string}
     */
    static get toolbox() {
        return {
            icon: '<svg width="20" height="20" aria-hidden="true" focusable="false" data-prefix="far" data-icon="keyboard" class="svg-inline--fa fa-keyboard fa-w-18" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M528 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h480c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm8 336c0 4.411-3.589 8-8 8H48c-4.411 0-8-3.589-8-8V112c0-4.411 3.589-8 8-8h480c4.411 0 8 3.589 8 8v288zM170 270v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm-336 82v-28c0-6.627-5.373-12-12-12H82c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm384 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zM122 188v-28c0-6.627-5.373-12-12-12H82c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm96 0v-28c0-6.627-5.373-12-12-12h-28c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h28c6.627 0 12-5.373 12-12zm-98 158v-16c0-6.627-5.373-12-12-12H180c-6.627 0-12 5.373-12 12v16c0 6.627 5.373 12 12 12h216c6.627 0 12-5.373 12-12z"></path></svg>',
            title: 'User Input'
        };
    }

    /**
     * Allow to press Enter inside the Warning
     *
     * @public
     * @returns {boolean}
     */
    static get enableLineBreaks() {
        return true;
    }

    /**
     * Default placeholder for warning title
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_TITLE_PLACEHOLDER() {
        return 'Question';
    }

    /**
     * Default placeholder for warning message
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_MESSAGE_PLACEHOLDER() {
        return 'Description & Help';
    }

    /**
     * User Input`s styles
     *
     * @returns {object}
     */
    get CSS() {
        return {
            baseClass: this.api.styles.block,
            wrapper: 'cdx-warning',
            title: 'cdx-warning__title',
            input: this.api.styles.input,
            message: 'cdx-warning__message',
        };
    }

    /**
     * Render plugin`s main Element and fill it with saved data
     *
     * @param {WarningData} data — previously saved data
     * @param {WarningConfig} config — user config for Tool
     * @param {object} api - Editor.js API
     * @param {boolean} readOnly - read-only mode flag
     */
    constructor({ data, config, api, readOnly }) {
        this.api = api;
        this.readOnly = readOnly;

        this.titlePlaceholder = config.titlePlaceholder || Warning.DEFAULT_TITLE_PLACEHOLDER;
        this.messagePlaceholder = config.messagePlaceholder || Warning.DEFAULT_MESSAGE_PLACEHOLDER;

        this.data = {
            title: data.title || '',
            message: data.message || '',
            id: data.id || uuidv4()
        };
    }

    /**
     * Create User Input container with inputs
     *
     * @returns {Element}
     */
    render() {
        const container = this._make('div', [this.CSS.baseClass, this.CSS.wrapper]);
        const title = this._make('div', [this.CSS.input, this.CSS.title], {
            contentEditable: !this.readOnly,
            innerHTML: this.data.title,
        });
        const message = this._make('div', [this.CSS.input, this.CSS.message], {
            contentEditable: !this.readOnly,
            innerHTML: this.data.message,
        });

        const type = this._make('select', ['field-type'], {});


        let option;

        option = document.createElement("option");
        option.value = 'Text Field';
        option.text = 'Text Field';
        type.appendChild(option);

        option = document.createElement("option");
        option.value = 'Text Area';
        option.text = 'Text Area';
        type.appendChild(option);

        title.dataset.placeholder = this.titlePlaceholder;
        message.dataset.placeholder = this.messagePlaceholder;

        container.appendChild(title);
        container.appendChild(message);
        container.appendChild(type);

        return container;
    }

    /**
     * Extract Warning data from User Input element
     *
     * @param {HTMLDivElement} warningElement - element to save
     * @returns {WarningData}
     */
    save(warningElement) {
        const title = warningElement.querySelector(`.${this.CSS.title}`);
        const message = warningElement.querySelector(`.${this.CSS.message}`);
        const fType = warningElement.querySelector(`.field-type`);

        console.log(fType, fType.value);

        return Object.assign(this.data, {
            title: title.innerHTML,
            message: message.innerHTML,
            fieldType: fType.value
        });
    }

    /**
     * Helper for making Elements with attributes
     *
     * @param  {string} tagName           - new Element tag name
     * @param  {Array|string} classNames  - list or name of CSS classname(s)
     * @param  {object} attributes        - any attributes
     * @returns {Element}
     */
    _make(tagName, classNames = null, attributes = {}) {
        const el = document.createElement(tagName);

        if (Array.isArray(classNames)) {
            el.classList.add(...classNames);
        } else if (classNames) {
            el.classList.add(classNames);
        }

        for (const attrName in attributes) {
            el[attrName] = attributes[attrName];
        }

        return el;
    }

    /**
     * Sanitizer config for User Input saved data
     *
     * @returns {object}
     */
    static get sanitize() {
        return {
            title: {},
            message: {},
        };
    }
}
